import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FieldExtensionComponentProps,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { describeTargets, GithubAccessStatus } from '../GithubAccessStatus';
import {
  GITOPS_REPO_DEFAULT,
  NEW_REPO_TEMPLATE_DEFAULT,
  repoCreationHint,
} from '../githubOrgAccess';
import { useGithubAccess } from '../useGithubAccess';
import { GITHUB_ACCESS_PENDING } from '../githubAccessValidation';

const HOST = 'github.com';
// GitHub orgs available as app targets. Driven by config (app-config:
// internalApps.githubOrgs) so adding an org is a config change, not a code
// change. The first entry is the default selection.
const DEFAULT_OWNERS = ['ds24-labs', 'hulkag'];
// Orgs where new repos are auto-permitted only under the `app-` prefix (legacy
// hulkag policy). Other orgs (e.g. ds24-labs) take a bare repo name.
const PREFIX_OWNERS = ['hulkag'];
// Orgs where members cannot create repositories themselves — creation is
// restricted to org owners, so a self-service run fails at repo creation with
// a 403. Config-driven (internalApps.restrictedGithubOrgs) to stay in step
// with internalApps.githubOrgs; GitHub's own answer, when it gives one, is
// preferred over this list — see cannotCreateRepos below.
const DEFAULT_RESTRICTED_OWNERS = ['hulkag'];
const PREFIXED_PATTERN = /^app-[a-z][a-z0-9-]*$/;
const BARE_PATTERN = /^[a-z][a-z0-9-]*$/;
const patternFor = (owner: string) =>
  PREFIX_OWNERS.includes(owner) ? PREFIXED_PATTERN : BARE_PATTERN;

interface UiOptions {
  secretsKey?: string;
  additionalScopes?: string[];
}

type ConflictState = 'unknown' | 'checking' | 'free' | 'taken';

const buildHelperText = (params: {
  repoName: string;
  repoNameValid: boolean;
  conflict: ConflictState;
  owner: string;
  requiresPrefix: boolean;
}): string => {
  const { repoName, repoNameValid, conflict, owner, requiresPrefix } = params;
  if (!repoName) return 'Set the App Name in the previous step.';
  if (!repoNameValid) {
    return requiresPrefix
      ? `⚠ Under "${owner}", repo names must match "app-<name>" — only that prefix is auto-permitted.`
      : `⚠ Repo name must be lowercase, start with a letter (a–z, 0–9, "-").`;
  }
  if (conflict === 'checking') return `Checking ${owner}/${repoName}…`;
  if (conflict === 'taken') {
    return `⚠ A Backstage component for ${owner}/${repoName} already exists. Pick a different name.`;
  }
  if (conflict === 'free') {
    // Deliberately not a ✓: this only says the name is unused in the catalog,
    // and a tick here reads as an all-clear for the whole step.
    return `${owner}/${repoName} — name not taken in Backstage.`;
  }
  return `Will create ${owner}/${repoName}`;
};

export const RepoNameFromApp = (
  props: FieldExtensionComponentProps<string, UiOptions>,
) => {
  const { onChange, formContext, uiSchema } = props;
  const appName: string | undefined = formContext?.formData?.name;

  const configApi = useApi(configApiRef);
  const allowedOwners = useMemo(
    () =>
      configApi.getOptionalStringArray('internalApps.githubOrgs') ??
      DEFAULT_OWNERS,
    [configApi],
  );
  const restrictedOwners = useMemo(
    () =>
      configApi.getOptionalStringArray('internalApps.restrictedGithubOrgs') ??
      DEFAULT_RESTRICTED_OWNERS,
    [configApi],
  );
  const newRepoTemplate =
    configApi.getOptionalString('internalApps.newRepoTemplate') ??
    NEW_REPO_TEMPLATE_DEFAULT;
  const gitopsRepo =
    configApi.getOptionalString('internalApps.gitopsRepo') ??
    GITOPS_REPO_DEFAULT;

  const [owner, setOwner] = useState<string>(allowedOwners[0]);

  // Whether the selected owner requires the `app-` prefix. The default repo
  // name and validation follow this, so switching org flips the prefix.
  const requiresPrefix = PREFIX_OWNERS.includes(owner);
  const namePattern = patternFor(owner);
  let defaultRepoName = '';
  if (appName) {
    defaultRepoName = requiresPrefix ? `app-${appName}` : appName;
  }

  const [repoName, setRepoName] = useState<string>(defaultRepoName);
  const userEditedRepo = useRef(false);

  useEffect(() => {
    if (!userEditedRepo.current) {
      setRepoName(defaultRepoName);
    }
  }, [defaultRepoName]);

  const repoUrl = repoName ? `${HOST}?owner=${owner}&repo=${repoName}` : '';

  const options = (uiSchema?.['ui:options'] as UiOptions | undefined) ?? {};
  const secretsKey = options.secretsKey ?? 'USER_OAUTH_TOKEN';
  const additionalScopes = useMemo(
    () => options.additionalScopes ?? ['workflow', 'repo'],
    [options.additionalScopes],
  );

  const catalogApi = useApi(catalogApiRef);
  const { setSecrets } = useTemplateSecrets();
  const [conflict, setConflict] = useState<ConflictState>('unknown');

  useEffect(() => {
    if (!repoName || !patternFor(owner).test(repoName)) {
      setConflict('unknown');
      return undefined;
    }
    setConflict('checking');
    const handle = setTimeout(async () => {
      try {
        const slug = `${owner}/${repoName}`;
        const { items } = await catalogApi.getEntities({
          filter: [
            { 'metadata.name': repoName },
            { 'metadata.annotations.github.com/project-slug': slug },
          ],
          fields: ['kind', 'metadata.name'],
        });
        setConflict(items.length > 0 ? 'taken' : 'free');
      } catch {
        setConflict('unknown');
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [owner, repoName, catalogApi]);

  // Everything the run touches, in run order: it generates the new repo from
  // the blueprint template (read on the blueprint, create in `owner`), then
  // opens the deployment PR on the gitops repo. SAML SSO is authorized per
  // organization and per token, so `owner` and the gitops org each need their
  // own check — one probe of the gitops repo would still let the run die at
  // repo creation. The new repo itself cannot be probed: it does not exist yet.
  const targets = [newRepoTemplate, owner, gitopsRepo];

  const onToken = useCallback(
    (token: string) => setSecrets({ [secretsKey]: token }),
    [setSecrets, secretsKey],
  );

  // Keyed on `owner`, so switching org re-probes rather than leaving an
  // answer that was about the previous organization standing.
  const { state, access, error, problem, authorizeUrl, authorize } =
    useGithubAccess({
      targets,
      scopes: additionalScopes,
      scmUrl: `https://${HOST}/${owner}`,
      auto: true,
      onToken,
    });

  // Report the value only once access is proven, so a run that would fail at
  // its very first GitHub call cannot start.
  //
  // The empty string is load-bearing, and `required` in the template is not:
  // JSON Schema `required` only asks that the property be present, and "" is
  // present, so AJV passes it. What actually blocks the step is this
  // extension's `validation` (see ./index.ts), and that only runs for keys
  // present in formData — the scaffolder's createAsyncValidators iterates
  // Object.entries(formData). Writing "" rather than undefined is what keeps
  // the key there for the validator to reject.
  useEffect(() => {
    if (state === 'ok') {
      onChange(repoUrl);
      return;
    }
    // While the probe is still running, say so rather than writing the same
    // "" that means "denied" — otherwise pressing Next before four GitHub
    // round trips have finished reports an access problem that does not
    // exist. A prefilled draft makes that the common case, not the rare one.
    onChange(state === 'error' ? '' : GITHUB_ACCESS_PENDING);
  }, [repoUrl, state, onChange]);

  // GitHub reports `members_can_create_repositories` only to org owners, so
  // its absence says nothing — fall back to the configured list.
  const ownerAccess = access.find(
    a => a.target === owner || a.target.startsWith(`${owner}/`),
  );
  const cannotCreateRepos =
    ownerAccess?.membersCanCreateRepos === false ||
    (ownerAccess?.membersCanCreateRepos === undefined &&
      restrictedOwners.includes(owner));

  const repoNameValid = !repoName || namePattern.test(repoName);
  const repoHelperText = buildHelperText({
    repoName,
    repoNameValid,
    conflict,
    owner,
    requiresPrefix,
  });

  return (
    <FormControl fullWidth margin="normal">
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Repository
      </Typography>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <TextField
          select
          label="Organisation"
          value={owner}
          onChange={e => setOwner(e.target.value)}
          error={cannotCreateRepos}
          style={{ minWidth: 180 }}
          SelectProps={{
            MenuProps: {
              MenuListProps: {
                sx: { display: 'flex', flexDirection: 'column' },
              },
            },
          }}
        >
          {allowedOwners.map(o => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </TextField>
        <Typography style={{ marginTop: 16 }}>/</Typography>
        <TextField
          label="Repository name"
          value={repoName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            userEditedRepo.current = true;
            setRepoName(e.target.value);
          }}
          error={!!repoName && (!repoNameValid || conflict === 'taken')}
          helperText={repoHelperText}
          style={{ flex: 1 }}
        />
      </div>
      <FormHelperText error={cannotCreateRepos}>
        {cannotCreateRepos
          ? `⚠ ${repoCreationHint(owner)}`
          : `Everyone in the org can create repositories in ${owner}.`}
      </FormHelperText>
      <div style={{ marginTop: 8 }}>
        <GithubAccessStatus
          state={state}
          access={access}
          error={error}
          problem={problem}
          authorizeUrl={authorizeUrl}
          targets={targets}
          onRetry={o => authorize(o).catch(() => undefined)}
          okMessage={`GitHub access authorized for ${describeTargets(
            targets,
          )} — the repository and pull requests will be created as you.`}
          connectLabel="Connect GitHub"
          requiredHint="Required: Backstage creates the repository and opens the pull requests as you, so it needs your GitHub access before this step can continue."
        />
      </div>
    </FormControl>
  );
};
